import { PracticeProgressData } from "../puzzles/dailyPractice";
import type { LearningStartingPoint } from "@chesshotel/types";

export type ChessStreak = Pick<
  PracticeProgressData,
  "today" | "completedToday" | "streak" | "totalDays" | "daysThisWeek" | "week"
> & {
  timezone: string;
  activityDates?: string[];
  longestStreak: number;
  milestones: { target: number; earned: boolean }[];
};
export type LearningStep = {
  kind: "resume" | "revisit" | "personal" | "collection" | "lesson";
  lessonSlug?: string;
  isNew?: boolean;
  personal?: boolean;
  puzzleId?: string;
  category?: string;
  moveIndex?: number;
  gameUrl?: string;
  explanation?: string;
  explanationKey?: string;
  fen?: string;
};
export type LearningOverview = {
  startingPoint?: LearningStartingPoint;
  chessStreak: ChessStreak;
  practice: PracticeProgressData;
  nextStep: LearningStep;
  discoveries: LearningStep[];
  weeklyGoal: {
    current: number;
    target: number;
    weekStart: string;
    completed: boolean;
  };
  personalPositions: number;
  rememberedPositions: number;
  duePositions: number;
  lessonsCompleted?: number;
  lessonsTotal?: number;
  lessonProgress?: { slug: string; completedExercises: number[] }[];
  openingProgress?: {
    slug: string;
    completedLines: unknown;
    completedChallengeLines: unknown;
    practiceLineProgress?: unknown;
    challengeLineProgress?: unknown;
    challengeCompleted?: boolean;
  }[];
  guest?: boolean;
  persistent?: boolean;
  revisitLessonSlug?: string;
};

export function learningPromptKey(data: LearningOverview) {
  const step = data.nextStep;
  if (step.kind === "resume") return "learning.promptResume";
  if (step.kind === "revisit") return "learning.promptRevisit";
  if (step.kind === "lesson")
    return step.isNew ? "learning.promptInsight" : "learning.promptLesson";
  if (step.kind === "personal")
    return step.isNew ? "learning.promptInsight" : "learning.promptPersonal";
  return data.practice.completedToday
    ? "learning.promptDone"
    : "learning.promptDaily";
}

export function learningTopicKey(category?: string) {
  const topics: Record<string, string> = {
    fork: "fork",
    pin: "pin",
    skewer: "skewer",
    checkmate: "mate",
    "back rank mate": "mate",
    "threaten checkmate": "mate",
    "hanging piece": "material",
    "win material": "material",
    development: "development",
    "develop queen early": "development",
  };
  return `learning.topics.${topics[category || ""] || "position"}`;
}

export function achievementTitleKey(key: string) {
  return `practiceAchievements.${
    key.startsWith("personal_week_") ? "personal_week" : key
  }.title`;
}

/** A completed daily session must not swallow a new personal recommendation. */
export function learningExerciseHref(data: LearningOverview, session: string) {
  const step = data.nextStep;
  if (step.kind === "lesson" && step.lessonSlug)
    return {
      pathname: `/learn/lessons/${step.lessonSlug}`,
      query: step.puzzleId
        ? { puzzle: step.puzzleId, source: "learning" }
        : { source: "learning" },
    };
  if (data.guest) return "/puzzles/themes";
  if (
    step.puzzleId &&
    (step.kind === "personal" ||
      (step.kind === "revisit" &&
        step.personal &&
        data.practice.completedToday))
  ) {
    return {
      pathname: `/puzzles/mistakes/practice/${step.puzzleId}`,
      query: { session, source: "learning" },
    };
  }
  if (step.kind === "collection" && data.practice.completedToday)
    return "/puzzles/themes";
  if (step.kind === "revisit" && !step.personal && data.practice.completedToday)
    return "/puzzles/themes";
  return "/puzzles/practice";
}
